import React, { useEffect, useMemo, useState } from "react";
import { FaFileExcel, FaSearch, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { axiosMaster } from "../api/axiosInstance";
import AddEditUserTypeMaster from "./AddEditUserTypeMaster";

const UserTypeMaster = () => {

  const [userList, setUserList] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  /* ================= FETCH USER TYPES ================= */

  const fetchUserTypes = async () => {
    try {
      setLoading(true);

      const res = await axiosMaster.get("/UserTypeMaster");

      setUserList(res.data || []);
    } catch (err) {
      console.error("UserType fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserTypes();
  }, []);

  /* ================= SEARCH ================= */

  const filteredData = useMemo(() => {
    const term = search.toLowerCase();

    return userList.filter((u) =>
      (u.userTypeName || "").toLowerCase().includes(term)
    );
  }, [userList, search]);

  /* ================= ACTIONS ================= */

  const handleAdd = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setEditData(row);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete?")) return;

    try {
      await axiosMaster.delete(`/UserTypeMaster/${id}`);
      fetchUserTypes();
    } catch (err) {
      console.error("Delete error", err);
    }
  };

  /* ================= EXPORT EXCEL ================= */

  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      UserTypeId: r.userTypeId,
      UserTypeName: `"${r.userTypeName || ""}"`,
      Status: r.status ? "Active" : "Inactive",
      EntryDate: r.entryDate,
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) => Object.values(e).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "UserTypeMaster.csv";
    link.click();
  };

  /* ================= UI ================= */

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-5 flex flex-col border border-slate-200 min-h-[calc(100vh-120px)]">

      {/* HEADER */}

      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">

        <h2 className="text-lg md:text-xl font-bold text-slate-800">
          User Type Master
        </h2>

        <div className="flex flex-wrap gap-2">

          {/* SEARCH */}

          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-slate-400 text-sm pointer-events-none" />
            <input
              type="text"
              placeholder="Search user type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* EXCEL */}

          <button
            onClick={exportExcel}
            disabled={!filteredData.length}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white
            ${
              !filteredData.length
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            <FaFileExcel />
            Excel
          </button>

          {/* ADD */}

          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <FaPlus />
            Add User Type
          </button>

        </div>
      </div>

      {/* TABLE */}

      <div className="flex-1 overflow-auto">

        <table className="w-full text-sm border-collapse">

          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="p-2 border text-left">User Type ID</th>
              <th className="p-2 border text-left">User Type Name</th>
              <th className="p-2 border text-left">Status</th>
              <th className="p-2 border text-left">Entry Date</th>
              <th className="p-2 border text-left">Action</th>
            </tr>
          </thead>

          <tbody>

            {loading ? (
              <tr>
                <td colSpan="5" className="text-center p-6">
                  Loading...
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center p-6">
                  No data found
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (
                <tr key={row.id || row.userTypeId}>
                  <td className="p-2 border">{row.userTypeId}</td>

                  <td className="p-2 border font-medium">
                    {row.userTypeName}
                  </td>

                  <td className="p-2 border">
                    {row.status ? "Active" : "Inactive"}
                  </td>

                  <td className="p-2 border">{row.entryDate}</td>

                  <td className="p-2 border">
                    <div className="flex gap-3">

                      <FaEdit
                        className="text-blue-600 cursor-pointer"
                        onClick={() => handleEdit(row)}
                      />

                      <FaTrash
                        className="text-red-600 cursor-pointer"
                        onClick={() => handleDelete(row.id)}
                      />

                    </div>
                  </td>

                </tr>
              ))
            )}

          </tbody>

        </table>

      </div>

      {/* MODAL */}

      {showModal && (
        <AddEditUserTypeMaster
          editData={editData}
          onClose={() => setShowModal(false)}
          onSuccess={() => fetchUserTypes()}
        />
      )}

    </div>
  );
};

export default UserTypeMaster;